/* eslint-disable no-param-reassign */
import { useEffect, useState } from 'react'

import AddressFields from './AddressFields'
import { getAddressString } from './common'
import EntryModeSelect from './EntryModeSelect'
import MapAndSearch from './MapAndSearch'

function GoogleMap(props) {
  const {
    id,
    label = null,
    placeholder = null,
    defaultEntryMode,
    enableManualEntry,
    enableSearch,
    addressDefinition,
    subDivisions = [],
    lat,
    lng,
    groupAddressFields,
    inputProps = {},
    searchProps = {},
    enableMap,
    apiKey,
    isMapInline,
    mapOptions = {},
    onChange,
    name,
    errorTextRequired,
    disabled,
    dataTestId,
    allowEmptyAutofill,
    hierarchyCountryCode,
  } = props

  let initialEntryMode = defaultEntryMode || 'search'
  if (!enableSearch && defaultEntryMode === 'search') initialEntryMode = 'manual'
  if (!enableManualEntry && defaultEntryMode === 'manual') initialEntryMode = 'search'

  const mapOnly = !enableManualEntry && !enableSearch && enableMap

  if (!enableManualEntry && !enableSearch && !enableMap) {
    throw new Error('GoogleMap: At least one of enableManualEntry, enableSearch or enableMap must be set')
  }

  const [addressValue, setAddressValue] = useState(null)
  const [coordinates, setCoordinates] = useState(null)
  const [entryMode, setEntryMode] = useState(initialEntryMode)

  useEffect(() => {
    if (addressDefinition) {
      const addressValueObject = Object.keys(addressDefinition).reduce((obj, key) => {
        obj[key] = addressDefinition[key].value
        return obj
      }, {})
      setAddressValue(addressValueObject)
    } else {
      setAddressValue(null)
    }
  }, [addressDefinition])

  useEffect(() => {
    if (lat && lng) {
      setCoordinates({
        lat: Number(lat),
        lng: Number(lng),
      })
    }
  }, [lat, lng])

  const handleOnChange = ({ newAddress, addressParts, newLat, newLng }, fieldKey) => {
    if (fieldKey === 'place') {
      setCoordinates({
        lat: newLat,
        lng: newLng,
      })
    }

    const newAddressData = {
      ...addressValue,
      ...newAddress,
    }

    setAddressValue(newAddressData)

    if (typeof onChange !== 'function') return

    onChange(
      {
        address: newAddressData,
        addressParts: addressParts || null,
        coordinates: {
          // biome-ignore lint/complexity/useOptionalChain: TODO
          lat: newLat || (coordinates && coordinates.lat) || null,
          // biome-ignore lint/complexity/useOptionalChain: TODO
          lng: newLng || (coordinates && coordinates.lng) || null,
        },
        value: getAddressString(newAddressData),
      },
      fieldKey
    )
  }

  const showEntryModeSelect = enableSearch && enableManualEntry && addressDefinition
  const enableMapAndSearch = (entryMode === 'search' || mapOnly) && !!apiKey
  const showAddressFields = entryMode === 'manual' && enableManualEntry && addressDefinition

  const isRequired =
    inputProps.isRequired ||
    searchProps.isRequired ||
    (addressDefinition
      ? Object.keys(addressDefinition).filter(key => {
          return !!addressDefinition[key].isRequired
        }).length > 0
      : false)

  const errorText =
    errorTextRequired ||
    inputProps.errorText ||
    searchProps.errorText ||
    (addressDefinition
      ? Object.keys(addressDefinition)
          .map(key => {
            return addressDefinition[key].errorText
          })
          .filter(m => !!m)
          .join(', ')
      : null)

  const mainInputProps = {
    ...inputProps,
    ...searchProps,
    isRequired,
    ...{
      label: label || null,
      placeholder: placeholder || searchProps.placeholder || 'Search for your address...',
    },
  }

  return (
    <>
      {!groupAddressFields && showEntryModeSelect && (
        <EntryModeSelect
          disabled={disabled}
          entryMode={entryMode}
          setEntryMode={setEntryMode}
          groupAddressFields={groupAddressFields}
          id={id}
        />
      )}
      {enableMapAndSearch && (
        <MapAndSearch
          disabled={disabled}
          id={id}
          apiKey={apiKey}
          addressValue={addressValue}
          coordinates={coordinates}
          mainInputProps={{ ...mainInputProps, errorText }}
          handleOnChange={handleOnChange}
          enableSearch={enableSearch}
          enableMap={enableMap}
          mapOptions={{
            isMapInline,
            ...mapOptions,
          }}
          showEntryModeSelect={showEntryModeSelect}
          setEntryMode={setEntryMode}
          groupAddressFields={groupAddressFields}
          allowEmptyAutofill={allowEmptyAutofill}
        />
      )}
      {showAddressFields && (
        <AddressFields
          disabled={disabled}
          name={name}
          addressDefinition={addressDefinition}
          addressValue={addressValue}
          subDivisions={subDivisions}
          groupAddressFields={groupAddressFields}
          inputProps={inputProps}
          mainInputProps={mainInputProps}
          handleOnChange={handleOnChange}
          showEntryModeSelect={showEntryModeSelect}
          setEntryMode={setEntryMode}
          id={id}
          dataTestId={dataTestId}
          hierarchyCountryCode={hierarchyCountryCode}
        />
      )}
    </>
  )
}

GoogleMap.defaultProps = {
  defaultEntryMode: 'search',
  enableManualEntry: false,
  enableSearch: false,
  enableMap: false,
  lat: 37.8199669,
  lng: 144.9834493,
  isMapInline: true,
  groupAddressFields: false,
  inputProps: {
    withDivider: false,
  },
  dataTestId: '',
  allowEmptyAutofill: false,
}

export default GoogleMap
